import React, { useEffect, useRef, useState } from "react";
import * as Yup from "yup";
import { MRT_ColumnDef } from "material-react-table";
import FormBuilder from "../../../../../../shared/components/form-builder/form-builder";
import {
  ITCProduct,
  ILastProcessorInfo,
  IAdditionalInfo,
  ITcProductRawMaterialInformation,
} from "../../../../tc-models/tc-req-dto/tc-certified-product-details.req_dto";
import { buyerSellerDetailsService } from "../../../../tc-service/buyer-seller-details.service";
import { dtrackItService } from "../../../../tc-service/dtrackit.service";
import { Utills } from "../../../../../../shared/app-configs/utilit";
import { useTransactionCertificateStore } from "../../../../../../store/transaction-certificates.store";

interface LastProcessorInfoProps {
  lastProcessorData: ILastProcessorInfo;
  setLastProcessorData: React.Dispatch<
    React.SetStateAction<ILastProcessorInfo>
  >;
  formRef: any;
}

const LastProcessorInfo: React.FC<LastProcessorInfoProps> = ({
  formRef,
  lastProcessorData,
  setLastProcessorData,
}) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [countries, setCountries] = useState([]);
  const toggleDrawer = () => {
    if (drawerOpen) resetForm();
    setDrawerOpen((prev) => !prev);
  };
  const [isTELabelEnabled, setTELabelEnabled] = useState(!lastProcessorData?.tcProductLastProcessorTeId || lastProcessorData?.tcProductLastProcessorTeId==="");
  const [isTEEnabled, setTEEnabled] = useState(true);
  const { setCountryCodes, getTCStandardGOTS } =
    useTransactionCertificateStore();

  const resetForm = () => {
    setLastProcessorData(lastProcessorData);
  };

  const handleConsigneeTEIdValueChange = (value: any) => {
    const fields = ["tcProductLastProcessorName"];
    if (value == null) {
      fields.forEach((field) => {
        formRef.current.setFieldValue(field, "");
      });
      setTELabelEnabled(true);
    } else {
      formRef.current.setFieldValue("tcProductLastProcessorTeId", value.TE_ID);
      formRef.current.setFieldValue(
        "tcProductLastProcessorName",
        value.account_name
      );
      if (value?.country) {
        formRef.current.setFieldValue(
          "tcProductLastProcessorCountry",
          value?.country
        );
      } 
      setTELabelEnabled(false);
    }

    // initialValues.consigneeTeId = value
    // setSelectedValue(value);
  };
  const handleProductLastProcessorNameChange = (value: any) => {
    const fields = ["tcProductLastProcessorTeId"];
    if (value == null) {
      fields.forEach((field) => {
        formRef.current.setFieldValue(field, "");
      });
      setTEEnabled(true);
    } else {
      formRef.current.setFieldValue("tcProductLastProcessorTeId", value.TE_ID);
      formRef.current.setFieldValue(
        "tcProductLastProcessorName",
        value.account_name
      );
      setTEEnabled(false);
    }
  };
  const getCountry = async () => {
    try {
      const result: any = await dtrackItService.getCountry();
      const countries = result.data.map((t: any) => ({
        value: t.code_1,
        label: t.description_1 + " (" + t.code_1 + ")",
      }));
      setCountries(countries);
      setCountryCodes(countries);
      // Utills.cl(`getCountry ${countries}`)
    } catch (err) {
      Utills.cl(`Error in Fetching Organic Farm Standard Code ${err}`);
    }
  };

  useEffect(() => {
    getCountry();
  }, []);
  const fetchTEDetallsFromAPI = async (
    value: string,
    offset: number,
    type: string,
    name?: string
  ): Promise<any[]> => {
    try {
      const queryParams: Record<string, any> = {
        query_param: type == "query" ? value : "",
        offset: offset,
        name: type == "name" ? value : "",
      };
      // Fetch TE Details using the getTEDetails function
      const response = await buyerSellerDetailsService.getTEDetails(
        queryParams
      );
      const data = response.data;
      return data;
    } catch (error) {
      console.error("Error fetching TE details:", error);
      throw error; // Re-throw error to handle it in the calling code
    }
  };
  const formConfig = {
    pageName: "certified-products",
    initialValues: lastProcessorData,
    validationSchema: Yup.object().shape({
      tcIsLastProcessorTECertitfied: Yup.string().nullable(),
      tcProductLastProcessorCountry: Yup.string().when([], {
        is: () => !getTCStandardGOTS, // If false, apply required validation
        then: (schema) =>
          schema
            .required("Country is required")
            .test(
              "not-dummy-na",
              "Country is required",
              (value: string | undefined | null) => {
                if (!value) return false;
                return value.toLowerCase() !== "nn";
              }
            ),
        otherwise: (schema) => schema.nullable(),
      }),
      tcProductLastProcessorLicenseNo: Yup.string().nullable(),
      tcProductLastProcessorName: Yup.string().nullable(),
      tcProductLastProcessorTeId: Yup.string().nullable(),
    }),
    sections: [
      {
        title: "Add Last Processor Information",
        fields: [
          {
            label: "Does the last processor have a TE ID",
            name: "tcIsLastProcessorTECertitfied",
            type: "radio",
            gridSize: { xs: 12, md: 12, lg: 12 },
            required: false,
            value: lastProcessorData.tcIsLastProcessorTECertitfied,
            events: {
              onChange: (value: any) => {
                if (value === "Y") {
                  formRef.current.setFieldValue(
                    "tcProductLastProcessorTeId",
                    ""
                  );
                  formRef.current.setFieldValue(
                    "tcProductLastProcessorName",
                    ""
                  );
                } else {
                  formRef.current.setFieldValue(
                    "tcProductLastProcessorTeId",
                    ""
                  );
                  formRef.current.setFieldValue(
                    "tcProductLastProcessorName",
                    ""
                  );
                }
              },
            },
            props: {
              options: [
                { value: "Y", label: "Yes" },
                { value: "N", label: "No" },
              ],
              placeholder: "Select TE Certification",
            },
          },
          {
            label: "Product Last Processor TE-ID",
            name: "tcProductLastProcessorTeId",
            type: "search",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: false,
            visible: (values: any) =>
              values.tcIsLastProcessorTECertitfied === "Y" && isTEEnabled,
            value: lastProcessorData.tcProductLastProcessorTeId,
            props: {
              onChange: handleConsigneeTEIdValueChange,
              restrictInputFormat: isTELabelEnabled,
              placeholder: "Enter TC Product Last Processor TE-ID",
              onSearch: (query: string, offset: number) => {
                const response = fetchTEDetallsFromAPI(query, offset, "query");
                return response;
              },
              displayKey: "TE_ID",
            },
          },
          {
            label: "Product Last Processor TE-ID",
            name: "tcProductLastProcessorTeId",
            type: "text",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: false,
            value: lastProcessorData.tcProductLastProcessorTeId,
            visible: (values: any) =>  {
              return values.tcIsLastProcessorTECertitfied === "Y" && !isTEEnabled;},
            props: {
              placeholder: "Enter TC Product Last Processor TE-ID",
            },
          },
          {
            label: "Product Last Processor Country/Area",
            name: "tcProductLastProcessorCountry",
            type: "select",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: !getTCStandardGOTS ? true : false,
            value: lastProcessorData.tcProductLastProcessorCountry, // Set initial value here
            props: {
              options: countries,
              enableSearch: true,
              placeholder: "Select TC Product Last Processor Country/Area",
              fullWidth: true,
            },
          },
          // {
          //   label: 'Product Last Processor Country',
          //   name: 'tcProductLastProcessorCountry',
          //   type: 'text',
          //   gridSize: { xs: 12, md: 4, lg: 4 },
          //   required: true,
          //   value: lastProcessorData.tcProductLastProcessorCountry,
          //   // disabled: (values: any) => values.tcIsLastProcessorTECertitfied === 'Y',
          //   props: {
          //     placeholder: 'Enter TC Product Last Processor Country',
          //   },
          // },
          {
            label: "Product Last Processor License No.",
            name: "tcProductLastProcessorLicenseNo",
            type: "text",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: false,
            value: lastProcessorData.tcProductLastProcessorLicenseNo,
            props: {
              placeholder: "Enter TC Product Last Processor License No.",
            },
          },
          {
            label: "Product Last Processor Name",
            name: "tcProductLastProcessorName",
            type: "search",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: false,
            value: lastProcessorData.tcProductLastProcessorName,
            visible: (values: any) =>
              values.tcIsLastProcessorTECertitfied === "Y",
            props: {
              placeholder: "Enter TC Product Last Processor Name",
              variant: "outlined",
              onChange: handleProductLastProcessorNameChange,
              displayKey: "account_name",
              onSearch: (query: string, offset: number) => {
                const response = fetchTEDetallsFromAPI(query, offset, "name");
                return response;
              },
              fullWidth: true,
            },
          },
          {
            label: "Product Last Processor Name",
            name: "tcProductLastProcessorName",
            type: "text",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: false,
            visible: (values: any) =>
              values.tcIsLastProcessorTECertitfied === "N",
            value: lastProcessorData.tcProductLastProcessorName,
            props: {
              placeholder: "Enter TC Product Last Processor Name",
            },
          },
        ],
      },
    ],
    handleSubmit: (values: any) => {
      Utills.cl("Form Submitted: Last Processor Info", values);
      setLastProcessorData(values);
      toggleDrawer();
    },
  };

  return (
    <>
      <FormBuilder
        formConfig={formConfig}
        onSubmit={formConfig.handleSubmit}
        formRef={formRef}
      />
    </>
  );
};

export default LastProcessorInfo;
